import { Button } from '@paulownia/shared/components/ui/button';
import { Input } from '@paulownia/shared/components/ui/input';
import { Label } from '@paulownia/shared/components/ui/label';
import { Textarea } from '@paulownia/shared/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@paulownia/shared/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@paulownia/shared/components/ui/select';
import { isNotificationPermissionDenied } from '@paulownia/shared/services/notifications';
import { Bell, AlertCircle, MapPin, Phone, CalendarX, Clock } from 'lucide-react';
import { DAY_OF_WEEK_LABELS } from '@paulownia/shared/types';
import type { CheckoutModalProps } from './types';

// Helper to format date for display
function formatAbsenceDate(dateStr: string): string {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
    });
}

export function CheckoutModal({
    open,
    onOpenChange,
    customerName,
    onCustomerNameChange,
    phoneNumber,
    onPhoneNumberChange,
    note,
    onNoteChange,
    pickupTime,
    onPickupTimeChange,
    wantsNotifications,
    onWantsNotificationsChange,
    tenantMarkets,
    selectedMarketId,
    onMarketChange,
    marketsLoading,
    selectedMarket,
    formattedPickupDate,
    timeSlots,
    submitting,
    onSubmit,
    absenceSkipInfo,
    orderClosureSkipInfo,
}: CheckoutModalProps) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col p-0">
                <DialogHeader className="px-6 pt-6 pb-2">
                    <DialogTitle className="text-2xl font-bold text-center">Finaliser la commande</DialogTitle>
                </DialogHeader>
                <form onSubmit={onSubmit} className="flex flex-col flex-1 overflow-hidden">
                    <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
                        {/* Market Selection */}
                        {marketsLoading ? (
                            <div className="text-center py-4 text-gray-500">
                                Chargement des marchés...
                            </div>
                        ) : tenantMarkets.length === 0 ? (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 flex items-start gap-3">
                                <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="font-medium">Aucun marché configuré</p>
                                    <p className="text-sm text-red-600">Impossible de passer commande pour le moment.</p>
                                </div>
                            </div>
                        ) : tenantMarkets.length === 1 && selectedMarket ? (
                            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                                <div className="flex items-start gap-3">
                                    <MapPin className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <div className="font-semibold text-gray-900">
                                            {selectedMarket.market.city}
                                        </div>
                                        <div className="text-sm text-gray-600">
                                            {DAY_OF_WEEK_LABELS[selectedMarket.market.dayOfWeek]} - {formattedPickupDate}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            {selectedMarket.market.openingTime} - {selectedMarket.market.closingTime}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : tenantMarkets.length > 1 ? (
                            <div className="space-y-2">
                                <Label className="text-lg flex items-center gap-2">
                                    <MapPin className="h-5 w-5 text-orange-600" />
                                    Marché
                                </Label>
                                <Select
                                    value={selectedMarketId || ''}
                                    onValueChange={(value) => {
                                        onMarketChange(value);
                                        onPickupTimeChange(''); // Reset time when market changes
                                    }}
                                >
                                    <SelectTrigger className="h-12 text-base">
                                        <SelectValue placeholder="Choisir un marché" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {tenantMarkets.map(tm => (
                                            <SelectItem key={tm.marketId} value={tm.marketId}>
                                                {tm.market.city} - {DAY_OF_WEEK_LABELS[tm.market.dayOfWeek]}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {selectedMarket && (
                                    <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                                        Retrait le <span className="font-medium">{formattedPickupDate}</span>
                                        <span className="text-gray-400 mx-2">•</span>
                                        {selectedMarket.market.openingTime} - {selectedMarket.market.closingTime}
                                    </div>
                                )}
                            </div>
                        ) : null}

                        {/* Absence period notice */}
                        {absenceSkipInfo && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                <div className="flex items-start gap-3">
                                    <CalendarX className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="font-medium text-amber-800">
                                            Absence du commerçant
                                        </p>
                                        <p className="text-sm text-amber-700 mt-1">
                                            Le commerçant est absent jusqu'au{' '}
                                            <span className="font-medium">{formatAbsenceDate(absenceSkipInfo.absenceEnd)}</span>.
                                            La prochaine date de retrait disponible est le{' '}
                                            <span className="font-medium">{formatAbsenceDate(absenceSkipInfo.skippedToDate)}</span>.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Order closure notice */}
                        {orderClosureSkipInfo && (
                            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                                <div className="flex items-start gap-3">
                                    <Clock className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="font-medium text-orange-800">
                                            Commandes fermées pour le marché de {orderClosureSkipInfo.formattedOriginalDate}
                                        </p>
                                        <p className="text-sm text-orange-700 mt-1">
                                            Les commandes ont fermé le{' '}
                                            <span className="font-medium">{orderClosureSkipInfo.formattedDeadline}</span>.
                                            Vous commandez pour{' '}
                                            <span className="font-medium">{orderClosureSkipInfo.formattedSkippedToDate}</span>.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label htmlFor="name" className="text-lg">Votre nom</Label>
                            <Input
                                id="name"
                                value={customerName}
                                onChange={e => onCustomerNameChange(e.target.value)}
                                placeholder="Ex: Dupont"
                                required
                                className="h-12 text-lg"
                                disabled={tenantMarkets.length === 0}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="phone" className="text-lg flex items-center gap-2">
                                <Phone className="h-4 w-4" />
                                Numéro de téléphone
                            </Label>
                            <Input
                                id="phone"
                                type="tel"
                                inputMode="numeric"
                                value={phoneNumber}
                                onChange={e => {
                                    const value = e.target.value;
                                    if (/^\d*$/.test(value)) {
                                        onPhoneNumberChange(value);
                                    }
                                }}
                                placeholder="Ex: 0612345678"
                                className="h-12 text-lg"
                                disabled={tenantMarkets.length === 0}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="note" className="text-lg">Note (optionnel)</Label>
                            <Textarea
                                id="note"
                                value={note}
                                onChange={e => onNoteChange(e.target.value)}
                                placeholder="Ajouter une note pour le commerçant..."
                                className="text-base resize-none"
                                rows={2}
                                disabled={tenantMarkets.length === 0}
                            />
                        </div>

                        <div className="space-y-3">
                            <Label className="text-lg">Heure de retrait</Label>
                            {timeSlots.length > 0 ? (
                                <div className="grid grid-cols-4 gap-2">
                                    {timeSlots.map((slot) => (
                                        <Button
                                            key={slot.time}
                                            type="button"
                                            variant={pickupTime === slot.time ? 'default' : 'outline'}
                                            size="lg"
                                            onClick={() => onPickupTimeChange(slot.time)}
                                            className={`text-sm font-medium h-12 touch-manipulation ${
                                                pickupTime === slot.time ? 'bg-green-600 hover:bg-green-700' : ''
                                            }`}
                                        >
                                            {slot.label}
                                        </Button>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-red-500 p-4 bg-red-50 rounded-lg text-center">
                                    Plus de créneaux disponibles aujourd'hui
                                </p>
                            )}
                        </div>

                        {/* Notification permission */}
                        {!isNotificationPermissionDenied() && (
                            <button
                                type="button"
                                onClick={() => onWantsNotificationsChange(!wantsNotifications)}
                                className={`w-full p-4 rounded-xl border-2 transition-all ${
                                    wantsNotifications
                                        ? 'bg-orange-50 border-orange-400 shadow-md'
                                        : 'bg-gray-50 border-gray-200 hover:border-orange-300'
                                }`}
                            >
                                <div className="flex items-center gap-4">
                                    <div className={`p-3 rounded-full ${
                                        wantsNotifications ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-500'
                                    }`}>
                                        <Bell className="h-6 w-6" />
                                    </div>
                                    <div className="flex-1 text-left">
                                        <p className={`font-semibold text-base ${
                                            wantsNotifications ? 'text-orange-700' : 'text-gray-700'
                                        }`}>
                                            Me notifier quand c'est prêt
                                        </p>
                                        <p className="text-sm text-gray-500">
                                            Recevez une alerte sur votre téléphone
                                        </p>
                                    </div>
                                    <div className={`w-12 h-7 rounded-full p-1 transition-colors ${
                                        wantsNotifications ? 'bg-orange-500' : 'bg-gray-300'
                                    }`}>
                                        <div className={`w-5 h-5 rounded-full bg-white shadow-md transition-transform ${
                                            wantsNotifications ? 'translate-x-5' : 'translate-x-0'
                                        }`} />
                                    </div>
                                </div>
                            </button>
                        )}
                    </div>

                    {/* Footer - fixed at bottom */}
                    <div className="px-6 py-4 border-t bg-white space-y-3">
                        <Button
                            type="submit"
                            size="lg"
                            className="w-full h-16 text-xl font-bold bg-green-600 hover:bg-green-700 active:scale-[0.98] transition-all shadow-lg"
                            disabled={submitting || !pickupTime || tenantMarkets.length === 0}
                        >
                            {submitting ? 'Envoi en cours...' : 'Confirmer la commande'}
                        </Button>
                        <button
                            type="button"
                            onClick={() => onOpenChange(false)}
                            className="w-full text-center text-gray-500 hover:text-gray-700 py-2 text-sm"
                        >
                            Annuler
                        </button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
